import React, { useEffect, useRef, useState } from 'react';

import { BaseButton, Chip, useIsMobile, useT } from '@repo/common';

const OPTION_LABELS_KR = {
  'Target Role': '희망 직무',
  Nationality: '국적',
  Phone: '전화번호',
  'E-mail': '이메일',
};

function AddPersonalInfoModal({ onClose, onSave }) {
  const t = useT();
  const isMobile = useIsMobile();

  const options = ['Target Role', 'Nationality', 'Phone', 'E-mail'];

  const [hoveredOption, setHoveredOption] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState([]);

  const [dragY, setDragY] = useState(0);
  const dragStartRef = useRef(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const toggleOption = (option) => {
    setSelectedOptions((prev) =>
      prev.includes(option)
        ? prev.filter((item) => item !== option)
        : [...prev, option],
    );
  };

  const getChipState = (option) => {
    if (selectedOptions.includes(option)) return 'selected';
    if (hoveredOption === option) return 'selected';
    return 'default';
  };

  const handleDragStart = (e) => {
    dragStartRef.current = e.touches ? e.touches[0].clientY : e.clientY;
  };

  const handleDragMove = (e) => {
    if (dragStartRef.current == null) return;
    const y = e.touches ? e.touches[0].clientY : e.clientY;
    setDragY(Math.max(0, y - dragStartRef.current));
  };

  const handleDragEnd = () => {
    if (dragStartRef.current == null) return;
    dragStartRef.current = null;
    if (dragY > 100) {
      onClose?.();
    } else {
      setDragY(0);
    }
  };

  return (
    <div
      className={`fixed inset-0 z-[2000] bg-black/60 flex ${
        isMobile ? 'items-end' : 'items-center justify-center'
      }`}
      onClick={onClose}
    >
      <div
        className={`flex flex-col items-center bg-white ${
          isMobile
            ? 'relative w-full rounded-t-[40px] px-6 pt-4 pb-10 animate-slideUp min-h-[390px]'
            : 'w-[460px] min-h-[330px] rounded-[30px]'
        }`}
        onClick={(e) => e.stopPropagation()}
        style={
          isMobile
            ? {
                transform: `translateY(${dragY}px)`,
                transition:
                  dragStartRef.current == null ? 'transform 0.2s ease' : 'none',
              }
            : undefined
        }
      >
        {isMobile && (
          <div
            className='flex pb-10 items-start w-full justify-between cursor-grab touch-none'
            onTouchStart={handleDragStart}
            onTouchMove={handleDragMove}
            onTouchEnd={handleDragEnd}
            onMouseDown={handleDragStart}
            onMouseMove={handleDragMove}
            onMouseUp={handleDragEnd}
            onMouseLeave={handleDragEnd}
          >
            <div className='mx-auto h-[6px] w-[46px] rounded-full bg-grayscale-gray02' />
          </div>
        )}

        {/* Header */}
        <div className='pb-3 tabletH:pt-6 tabletH:pb-[18px] typo-title1 tabletH:typo-b16Strong text-text-primary w-full tabletH:w-auto'>
          {t('Add more info', '추가 정보 입력')}
        </div>

        {/* Chips */}
        <div className='w-full flex tabletH:flex-none tabletH:px-10'>
          <div className='flex flex-wrap tabletH:flex-nowrap tabletH:flex-col items-start tabletH:items-center justify-start tabletH:justify-center gap-2 tabletH:gap-3 w-full'>
            {options.map((option) => (
              <Chip
                key={option}
                size={isMobile ? 'l' : 'm'}
                hideIcon
                state={getChipState(option)}
                onMouseEnter={() => setHoveredOption(option)}
                onMouseLeave={() => setHoveredOption(null)}
                onClick={() => toggleOption(option)}
              >
                {t(option, OPTION_LABELS_KR[option])}
              </Chip>
            ))}
          </div>
        </div>

        <div
          className={`flex gap-3 w-full ${
            isMobile
              ? 'absolute bottom-0 left-0 p-4 border-t border-grayscale-gray02'
              : 'mt-6 px-10 pb-[30px]'
          }`}
        >
          {!isMobile && (
            <BaseButton
              type='text'
              size='l'
              width='100%'
              hideIcon
              onClick={onClose}
              bgclass='bg-grayscale-gray02'
            >
              {t('Cancel', '취소')}
            </BaseButton>
          )}

          <BaseButton
            type='primary'
            size='l'
            width='100%'
            hideIcon
            disabled={selectedOptions.length === 0}
            onClick={() => onSave(selectedOptions)}
          >
            {t('Add', '추가')}
          </BaseButton>
        </div>
      </div>
    </div>
  );
}

export default AddPersonalInfoModal;
